import type { FC } from 'react';
import React, { Fragment } from 'react';
import { styled } from '@compiled/react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import { token } from '@atlaskit/tokens';
import { N0, N40 } from '@atlaskit/theme/colors';
import Button from '@atlaskit/button/standard-button';
import CloseIcon from '@atlaskit/icon/core/migration/close--editor-close';

import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { ErrorDisplay } from '@confluence/error-boundary';

import { onboardingFeedHeaderCard } from '../feedConstants';

import FollowingOnboardingImageDark from './images/FeedOnboardingMessageDark.svg';
import FollowingOnboardingImageLight from './images/FeedOnboardingMessageLight.svg';

export const i18n = defineMessages({
	headerTitle: {
		id: 'feed.header.title',
		defaultMessage: 'We’re keeping you in the loop',
		description: 'Title header from feed onboarding message',
	},
	headerDescription: {
		id: 'feed.header.description',
		defaultMessage:
			'Stay in-the-know by following people and spaces. Their activity will show up in your feed, but you won’t receive email notifications about it. Add to, or edit, your feed anytime.',
		description:
			"Description for feed onboarding message. 'they' being the people followed from the title message",
	},
	editFeed: {
		id: 'feed.header.editFeed',
		defaultMessage: 'Edit feed',
		description: 'Button from feed onboarding message to bring up user settings modal',
	},
	learnMore: {
		id: 'feed.header.learnMore',
		defaultMessage: 'Learn more',
		description:
			"Link from feed onboarding message that links to the What's New page for the next-gen feed project",
	},
	closeMessageLabel: {
		id: 'feed.header.closeLabel',
		defaultMessage: 'Close message box',
		description: 'label for the close icon to dismiss the message box',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyContainer = styled.div({
	boxSizing: 'border-box',
	border: `1px solid ${token('color.border', N40)}`,
	borderRadius: token('space.100', '8px'),
	background: token('elevation.surface', N0),
	display: 'flex',
	marginBottom: token('space.300', '24px'),
	padding: `${token('space.300', '24px')} ${token('space.300', '24px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	flexGrow: 1,
	marginRight: token('space.250', '20px'),
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleText = styled.h3({
	fontWeight: token('font.weight.regular'),
	marginBottom: token('space.100', '8px'),
});

const descriptionTextStyles = xcss({
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OnBoardingImage = styled(Image)({
	maxWidth: '200px',
	maxHeight: '160px',
	flexShrink: 0,
	alignSelf: 'flex-end',
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonContainer = styled.div({
	flexShrink: 0,
});

type FeedOnboardingMessageProps = {
	openActivityFilterModal: () => void;
	servingRecommendations: boolean;
} & WrappedComponentProps;

const FeedOnboardingMessageComponent: FC<FeedOnboardingMessageProps> = ({
	intl,
	openActivityFilterModal,
	servingRecommendations,
}) => {
	const [showFeature, stop, error] = useFeatureDiscovery(onboardingFeedHeaderCard);

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (!showFeature || !servingRecommendations) {
		return null;
	}

	return (
		<BodyContainer data-testid="feed-onboarding-message">
			<MessageContainer>
				<TitleText>
					<FormattedMessage {...i18n.headerTitle} />
				</TitleText>
				<Box xcss={descriptionTextStyles}>
					<FormattedMessage {...i18n.headerDescription} />{' '}
				</Box>
				<Fragment>
					<Button onClick={openActivityFilterModal}>
						<FormattedMessage {...i18n.editFeed} />
					</Button>
				</Fragment>
			</MessageContainer>

			<OnBoardingImage
				src={FollowingOnboardingImageLight}
				srcDark={FollowingOnboardingImageDark}
				width="200"
				height="160"
			/>

			<CloseButtonContainer>
				<Button
					iconBefore={<CloseIcon label={intl.formatMessage(i18n.closeMessageLabel)} />}
					appearance="subtle"
					spacing="none"
					onClick={stop}
				/>
			</CloseButtonContainer>
		</BodyContainer>
	);
};

export const FeedOnboardingMessage = injectIntl(FeedOnboardingMessageComponent);
